/* eslint-disable max-classes-per-file */
import { DayEnum } from '@rover/rsdk/src/apiClient/latest/model/dayEnum';
import { CONCEPTS } from '@rover/rsdk/src/concepts';
import {
  CTAButtonClickEvent,
  DatePickerEvent,
  MultiSelectFieldEvent,
  SingleSelectFieldEvent,
  TextFieldEvent,
} from '@rover/shared/js/eventstream/FormFieldAnalytics';
import type { Day } from '@rover/types/src/datetime/Day';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user updates the pet type field
 *
 * &nbsp;
 */
export class UpdatePetType extends MultiSelectFieldEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-update-pet-type';

  eventProperties: { cat: boolean; dog: boolean };

  constructor(eventProperties: { cat: boolean; dog: boolean }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user opens the service type dropdown, carrying whichever service was
 * already selected. Mobile search hero only — desktop lays the services out inline, so
 * it has no dropdown to open
 * [Event Spec M2 Intuitive mWeb Homepage Form](https://roverdotcom.atlassian.net/wiki/spaces/BKS/pages/3612672002/Event+Spec+M2+Intuitive+mWeb+Homepage+Form)
 *
 * &nbsp;
 */
export class ServiceTypeDropdownClickEvent extends CTAButtonClickEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-servicetype-dropdown-click';

  eventProperties: { serviceType?: string };

  constructor(eventProperties: { serviceType?: string }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user selects a service in the service type field, from both the desktop
 * and mobile search hero
 * [Homepage Event Overview](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4701061124/Homepage+Event+Overview)
 *
 * &nbsp;
 */
export class UpdateServiceType extends SingleSelectFieldEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-update-service-type';

  eventProperties: { serviceType: string };

  constructor(eventProperties: { serviceType: string }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user adds an address to the location field, from both the desktop and
 * mobile search hero
 * [Homepage Event Overview](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4701061124/Homepage+Event+Overview)
 *
 * &nbsp;
 */
export class UpdateLocation extends TextFieldEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-update-location';

  eventProperties: { value: string };

  constructor(eventProperties: { value: string }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user updates the pet size field
 *
 * &nbsp;
 */
export class UpdatePetSize extends MultiSelectFieldEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-update-size';

  eventProperties: {
    smallDogs: boolean;
    mediumDogs: boolean;
    largeDogs: boolean;
    giantDogs: boolean;
  };

  constructor(eventProperties: {
    smallDogs: boolean;
    mediumDogs: boolean;
    largeDogs: boolean;
    giantDogs: boolean;
  }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user updates the date picker field
 *
 * &nbsp;
 */
export class UpdateDates extends DatePickerEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-select-dates';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user toggles the frequency field
 *
 * &nbsp;
 */
export class UpdateFrequency extends SingleSelectFieldEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-update-frequency';

  eventProperties: { frequency: HomepageSearchFilters['frequency'] | '' };

  constructor(eventProperties: { frequency: HomepageSearchFilters['frequency'] | '' }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user updates the repeat day(s) when frequency is set to "repeat"
 *
 * &nbsp;
 */
export class UpdateRepeatDay extends MultiSelectFieldEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-update-repeat-day';

  // eslint-disable-next-line class-methods-use-this
  private formatSelectedDays(selectedDays: Day[]): Record<DayEnum, boolean> {
    const days = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };
    selectedDays.forEach(({ name }) => {
      days[name.toLowerCase()] = true;
    });
    return days;
  }

  eventProperties: Record<DayEnum, boolean>;

  constructor(eventProperties: { selectedDays: Day[] }) {
    super(eventProperties);
    this.eventProperties = this.formatSelectedDays(eventProperties.selectedDays);
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires when a user clicks the Search button in the desktop or mobile hero. Sent as a
 * cross page event, so it lands on the next page load rather than before the search
 * navigation
 * [Homepage Event Overview](https://roverdotcom.atlassian.net/wiki/spaces/PROD/pages/4701061124/Homepage+Event+Overview)
 *
 * &nbsp;
 */
export class HomepageCtaClickEvent extends CTAButtonClickEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'homepage-search-form-cta-click';
}

/**
 * &nbsp;
 *
 * # Description
 * Fires from the shared "Services for every pet" section, on any page that renders it
 *
 * Fires when a user clicks on a service link in the "Services for every pet" section
 *
 * &nbsp;
 */
export class ServiceLinkClickEvent extends CTAButtonClickEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'service-link-click';

  eventProperties: { serviceType: string };

  constructor(eventProperties: { serviceType: string }) {
    const observabilityTags = {
      serviceType: eventProperties.serviceType,
    };
    super(eventProperties, observabilityTags);
    this.eventProperties = eventProperties;
  }
}

export class RebookClickEvent extends CTAButtonClickEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'rebook-provider-rebook-click';

  eventProperties: { providerId: string; rank: string; serviceType: string };

  constructor(eventProperties: { providerId: string; rank: string; serviceType: string }) {
    const observabilityTags = {
      serviceType: eventProperties.serviceType,
    };
    super(eventProperties, observabilityTags);
    this.eventProperties = eventProperties;
  }
}

/**
 * &nbsp;
 *
 * # Description
 * Fires from the shared "Service Overview" component, on any page that renders it
 *
 * Fires when a user clicks on a service category tab in the "Service Overview" component
 *
 * &nbsp;
 */
export class ServiceCategoryTabClickEvent extends CTAButtonClickEvent {
  concept = CONCEPTS.marketing.growth;

  eventName = 'service-category-tab-click';

  eventProperties: { tabId: string };

  constructor(eventProperties: { tabId: string }) {
    super(eventProperties);
    this.eventProperties = eventProperties;
  }
}
